/**
 * DELEGATE — the personalised source-recommendations read moved to
 * `@heidi/evidence` at the `evidence/source` carve. `GET /evidence/source-recommendations`
 * still returns sources matched to the user's country and specialty; the request
 * is unchanged (the `@heidi/api/client` registry hands back the same
 * `scribeHttpV2` instance the old module imported directly).
 *
 * What stays here is the app-resolved gate. The pre-move hook composed
 * `!!token && enabled && !isKillSwitchOn` inline — a Rematch auth read and a
 * KILL-SWITCH flag getter mixed with the caller's own argument inside a data
 * hook. Both app terms resolve here and cross as one boolean, in the legacy
 * order, so the kill switch keeps its defining property: it forces the gate
 * `false` no matter what a caller passes.
 *
 * The gate also carries the SURFACE term. `GET /v2/evidence/source-recommendations`
 * is part of ml-scribe's cloud-only evidence aggregate, so on an on-prem build
 * there is no such route and every call 404s — the read is disabled at the
 * source rather than left to fail per render.
 *
 * The signature is unchanged, so no call site moves.
 *
 * `SOURCE_RECOMMENDATIONS_QUERY_KEY` is DECLARED in `@heidi/evidence/contracts`
 * and re-exported here name-intact. Never re-declare the literal.
 */
import { useIsSourceRecommendationsKillSwitchEnabled } from '@/features/flags';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { isEvidenceApiAvailable } from '@/utils/backendSurfaces';
import { useQuerySourceRecommendations as useQuerySourceRecommendationsCore } from '@heidi/evidence/data';

export { SOURCE_RECOMMENDATIONS_QUERY_KEY } from '@heidi/evidence/contracts';
export type {
  RecommendedSource,
  SourceRecommendationsResponse,
} from '@heidi/evidence/contracts';

export function useQuerySourceRecommendations(enabled = true) {
  const { isAuthenticated } = useIsAuthenticated();
  const isKillSwitchOn = useIsSourceRecommendationsKillSwitchEnabled();

  return useQuerySourceRecommendationsCore({
    enabled: isEvidenceApiAvailable && isAuthenticated && enabled && !isKillSwitchOn,
  });
}
